import {
    Typography,
    Box,
    IconButton,
    TextField,
    List,
    ListItem,
    ListItemIcon,
    Checkbox,
    ListItemText,
    Button,
    CircularProgress,
    Card,
    Divider,
    InputAdornment,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { RootState, useAppDispatch } from "../../../redux/store";
import { SwappableEmployee } from "../../../types/state/swap";
import { useSelector } from "react-redux";
import { DatetimeDetail, ShiftCardProps, ShiftType, SwapEmployeeV2 } from "../../../types";
import React from "react";
import { updateSelectedSwapEmployees } from "../../../redux/features/schedule/ScheduleSlice";
import { DateTimeUtil } from "../../../utils";
import { DatePicker } from "@mui/x-date-pickers";
import { Clear } from "@mui/icons-material";

const SelectionForm: React.FC<{
    startDate: Date | null;
    endDate: Date | null;
    handleStartDateChange: (date: Date | null) => void;
    handleEndDateChange: (date: Date | null) => void;
    handleNameSearch: (name: string) => void;
    onSuccess: () => void;
    onSearch: () => void;
}> = ({ startDate, endDate, handleEndDateChange, handleStartDateChange, onSuccess, handleNameSearch, onSearch }) => {
    const selectedShift = useSelector((state: any) => state.schedule.selectedShift) as ShiftType;
    const swappableEmployees = useSelector((state: RootState) => state.schedule.swappableEmployees);
    const fetchingEmployeeStatus = useSelector((state: any) => state.schedule.requestState) as string;
    const [grouped, setGrouped] = React.useState<Map<number, SwapEmployeeV2[]>>(new Map());
    const [selectedEmployees, setSelectedEmployees] = React.useState<Set<SwapEmployeeV2>>(new Set());
    const dispatch = useAppDispatch();
    const [startDetails, setStartDetails] = React.useState<DatetimeDetail>();
    const [searchValue, setSearchValue] = React.useState("");
    // Create refs for both start and end date inputs
    const startInputRef = useRef<HTMLInputElement>(null); // Ref type is HTMLInputElement
    const endInputRef = useRef<HTMLInputElement>(null); // Ref type is HTMLInputElement
    const [startAnchorEl, setStartAnchorEl] = React.useState<HTMLElement | null>(null);
    const [endAnchorEl, setEndAnchorEl] = React.useState<HTMLElement | null>(null);
    const actionConsecutiveDays = useSelector((state: RootState) => state.schedule.actionConsecutiveDays);
    const getEffectiveActionConsecutiveDays = actionConsecutiveDays > 1 ? actionConsecutiveDays - 1 : undefined;

    useEffect(() => {
        // Ensure the ref is populated before using it as anchorEl
        if (startInputRef.current) {
            setStartAnchorEl(startInputRef.current);
        }
        if (endInputRef.current) {
            setEndAnchorEl(endInputRef.current);
        }
    }, []);

    useEffect(() => {
        setStartDetails(getDatetimeDetails(selectedShift?.osEmpStartDateTime!));
    }, [selectedShift]);

    useEffect(() => {
        const grouped = groupEmployeesWithSameName();
        setGrouped(grouped);

        // setDateRange([filterStartDate, filterEndDate]);
    }, [swappableEmployees]);

    useEffect(() => {
        dispatch(updateSelectedSwapEmployees(Array.from(selectedEmployees)));
    }, [selectedEmployees]);

    const groupEmployeesWithSameName = (): Map<number, SwapEmployeeV2[]> => {
        const today = new Date().toUTCString();
        const employeesMap = new Map<number, SwapEmployeeV2[]>();
        swappableEmployees.forEach((employee) => {
            if (!DateTimeUtil.isAfter(new Date().toISOString(), employee.shift_start_date)) {
                if (employeesMap.has(Number(employee.emp_id!))) {
                    employeesMap.get(Number(employee.emp_id!))?.push(employee);
                } else {
                    employeesMap.set(Number(employee.emp_id!), [employee]);
                }
            }
        });

        return employeesMap;
    };

    // Check if an employee is selected by comparing their id
    const isEmployeeSelected = useCallback(
        (employee: SwapEmployeeV2) => {
            return Array.from(selectedEmployees).some(
                (selectedEmployee) => selectedEmployee.schedule_id == employee.schedule_id
            );
        },
        [selectedEmployees]
    );

    // Optimized handleCheckboxChangeV2 to handle adding/removing SwappableEmployee objects
    const handleCheckboxChangeV2 = useCallback((employee: SwapEmployeeV2, isChecked: boolean) => {
        setSelectedEmployees((prevSelected) => {
            const newSelected = new Set(prevSelected);
            if (isChecked) {
                // Add employee if not already in the set (based on id comparison)
                if (
                    !Array.from(newSelected).some(
                        (selectedEmployee) => selectedEmployee.schedule_id == employee.schedule_id
                    )
                ) {
                    newSelected.add(employee);
                }
            } else {
                // Remove the employee based on id
                newSelected.forEach((selectedEmployee) => {
                    if (selectedEmployee.schedule_id === employee.schedule_id) {
                        newSelected.delete(selectedEmployee);
                    }
                });
            }
            return newSelected;
        });
    }, []);

    const groupedEmployees = (() => {
        const grouped = new Map<number, SwapEmployeeV2[]>();
        swappableEmployees.forEach((employee) => {
            if (!DateTimeUtil.isAfter(new Date().toISOString(), employee.shift_start_date)) {
                if (!grouped.has(Number(employee.emp_id!))) {
                    grouped.set(Number(employee.emp_id!), []);
                }
                grouped.get(Number(employee.emp_id!))?.push(employee);
            }
        });
        return grouped;
    })();

    const renderAvailableEmployees = () => {
        return (
            <List dense>
                {Array.from(groupedEmployees).map(([employeeID, shifts]) => (
                    <Box
                        key={employeeID}
                        sx={{
                            border: "1px solid rgba(0, 0, 0, 0.12)",
                            padding: 1,
                            marginTop: 1,
                            backgroundColor: "#f9f9f9",
                        }}
                    >
                        <Typography variant="body2" gutterBottom>
                            {`${shifts[0].employee_name}`}
                        </Typography>
                        <Box
                            sx={{
                                height: shifts.length > 3 ? "clamp(100px, 50vh, 200px)" : "auto",
                                overflow: "auto",
                            }}
                        >
                            {shifts.map((shift) => (
                                <ListItem key={shift.schedule_id} button>
                                    <ListItemIcon>
                                        <Checkbox
                                            size="small"
                                            checked={isEmployeeSelected(shift)}
                                            onChange={(e) => handleCheckboxChangeV2(shift, e.target.checked)}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`${DateTimeUtil.formatDateTimeRangeUtc(
                                            `${shift.shift_start_date} ${shift.shift_start_time}`,
                                            `${shift.shift_end_date} ${shift.shift_end_time}`
                                        )} ${
                                            getEffectiveActionConsecutiveDays
                                                ? `+ ${getEffectiveActionConsecutiveDays} days`
                                                : ""
                                        } `}
                                    />
                                </ListItem>
                            ))}
                        </Box>
                    </Box>
                ))}
            </List>
        );
    };

    // Reusable function to render a DatePicker
    const renderDatePicker = useCallback(
        (
            label: string,
            value: Date | null,
            onChange: (date: Date | null) => void,
            boxRef: React.RefObject<HTMLDivElement>
        ) => {
            return (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Box sx={{ minWidth: 150, height: 50 }} ref={boxRef}>
                        <DatePicker
                            label={label}
                            value={value}
                            onChange={onChange}
                            slots={{
                                textField: (params) => <TextField {...params} size="small" />, // Handles the input field
                            }}
                            slotProps={{
                                popper: {
                                    anchorEl: boxRef.current,
                                    modifiers: [
                                        {
                                            name: "flip",
                                            enabled: false, // Disable flipping to avoid positioning issues
                                        },
                                        {
                                            name: "preventOverflow",
                                            options: {
                                                boundary: "viewport", // Ensure it stays within the viewport
                                            },
                                        },
                                        {
                                            name: "hide",
                                            enabled: false, // Disable hiding logic that could cause `reference-hidden`
                                        },
                                    ],
                                },
                            }}
                        />
                    </Box>
                </LocalizationProvider>
            );
        },
        []
    );

    const getDatetimeDetails = (dateTime: string): DatetimeDetail => {
        const date = new Date(dateTime);
        return {
            month: date.toLocaleString("default", { month: "long" }),
            monthNumber: date.getMonth(),
            day: date.toLocaleString("default", { day: "numeric" }),
            dayNumber: date.getDate(),
            year: date.getFullYear(),
            hour: date.getHours(),
            minute: date.getMinutes(),
            second: date.getSeconds(),
            timezone: date.toTimeString().split(" ")[1],
        };
    };

    const ShiftCard: React.FC<ShiftCardProps> = ({ startDetails, selectedShift, actionConsecutiveDays }) => {
        return (
            <Card elevation={0} className="flex items-center p-4 mb-2 mt-1">
                {/* Month & Day */}
                <div className="flex items-center gap-2 text-green-600 text-md">
                    <span>{startDetails?.month} {startDetails?.dayNumber}</span>
                    {getEffectiveActionConsecutiveDays && (
                        <span className="text-blue-600">+ {getEffectiveActionConsecutiveDays} days</span>
                    )}
                    <span>
                        {selectedShift?.osEmpStartDateTime
                            ? DateTimeUtil.getFormattedTimeUtc(selectedShift.osEmpStartDateTime)
                            : "N/A"} - {selectedShift?.osEmpEndDateTime
                                ? DateTimeUtil.getFormattedTimeUtc(selectedShift.osEmpEndDateTime)
                                : "N/A"}
                    </span>
                </div>

                {/* Date Time Range + Consecutive Days (in the same row) */}
                {/* <Box className="flex items-center gap-1 flex-1 border-0">
                    <Typography variant="body2">
                        {selectedShift?.osEmpStartDateTime
                            ? DateTimeUtil.getFormattedTimeUtc(selectedShift.osEmpStartDateTime)
                            : "N/A"}{" "}
                        -{" "}
                        {selectedShift?.osEmpEndDateTime
                            ? DateTimeUtil.getFormattedTimeUtc(selectedShift.osEmpEndDateTime)
                            : "N/A"}
                    </Typography>
                    {getEffectiveActionConsecutiveDays && (
                        <Typography sx={{ color: "#2196F3" }}>+ {getEffectiveActionConsecutiveDays} days</Typography>
                    )}
                </Box> */}
            </Card>
        );
    };

    return (
        <>
            {/* <Button onClick={test}>Test</Button> */}
            {/* <Typography variant="h6">Trade Shift</Typography> */}
            <Box
                sx={{
                    // border: "1px solid rgba(0, 0, 0, 0.12)",
                    // padding: 1,
                    marginTop: 1,
                    backgroundColor: "#ffffff",
                }}
            >
                {/* Shift Details Badge */}
                <ShiftCard
                    actionConsecutiveDays={actionConsecutiveDays}
                    selectedShift={selectedShift}
                    startDetails={startDetails}
                />

                {/* Date pickers and search button */}
                <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 2, gap: 2, height: 50 }}>
                    {/* Start Date Picker */}
                    {renderDatePicker("Start", startDate, handleStartDateChange, startInputRef)}

                    {/* End Date Picker */}
                    {renderDatePicker("End", endDate, handleEndDateChange, endInputRef)}

                    {/* Search Button */}
                    <Button variant="contained" color="primary" onClick={onSearch} sx={{ height: "100%" }}>
                        Search
                    </Button>
                </Box>
                <Typography variant="body2" gutterBottom>
                    {selectedEmployees.size} Selected Covers:
                </Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    placeholder="Employee search..."
                    size="small"
                    value={searchValue}
                    onChange={(e) => {
                        const value = (e.target as HTMLInputElement).value;
                        setSearchValue(value);
                        // handleNameSearch(value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            handleNameSearch(searchValue); // Trigger search on Enter
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {searchValue && (
                                    <IconButton
                                        onClick={() => {
                                            setSearchValue(""); // Clear the input value
                                            handleNameSearch(""); // Trigger search with empty string
                                        }}
                                    >
                                        <Clear />
                                    </IconButton>
                                )}
                            </InputAdornment>
                        ),
                    }}
                />
                {fetchingEmployeeStatus === "PENDING" ? (
                    <>
                        <CircularProgress
                            sx={{
                                display: "block",
                                margin: "auto",
                                color: "blue",
                            }}
                        />
                    </>
                ) : (
                    renderAvailableEmployees()
                )}
                <Box textAlign="right" marginTop={1}>
                    <Button variant="contained" color="primary" size="small" onClick={onSuccess}>
                        Continue
                    </Button>
                </Box>
            </Box>
        </>
    );
};

export default SelectionForm;
